import { useMemo } from 'react';

import { amountToBaseUnits } from '@change-corgi/core/intl';
import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useAvailablePaymentMethods, usePromotePageData, usePromotePageState } from 'src/app/pages/promote/pageContext';
import type { PaymentRequestButton } from 'src/app/shared/components/payments';

import { useSharedHooks } from '../../../hooks/useSharedHooks';

type Result = Omit<Parameters<typeof PaymentRequestButton>[0], 'paymentType'> & {
	paymentType: 'applePay' | 'googlePay' | null;
};

export function useStripeWallet(): Result {
	const [{ amount }, { clearLoading }] = usePromotePageState();
	const {
		data: {
			currency: { code: currencyCode },
			petition: { ask },
		},
	} = usePromotePageData();

	const { beforeToken, withToken, validate, prePaymentChecks } = useSharedHooks();
	const { paymentMethods } = useAvailablePaymentMethods();

	const track = useTracking();
	const { translate } = useI18n();

	const paymentType = useMemo(() => {
		if (paymentMethods.includes('applePay')) {
			return 'applePay';
		}
		if (paymentMethods.includes('googlePay')) {
			return 'googlePay';
		}
		return null;
	}, [paymentMethods]);

	return {
		beforeToken,
		withToken,
		validate,
		prePaymentChecks,
		onCancel: () => {
			clearLoading();
			void track('payment_request_cancel', { ...(paymentType && { payment_type: paymentType }) });
		},
		amount: amountToBaseUnits(amount, currencyCode),
		paymentType,
		currencyCode,
		label: translate(
			'fe.components.payment_request_button.line_item_description.promotion', // TODO: move this i18n key to corgi namespace
			{ petition_title: ask },
		),
	};
}
